import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { SpacePicker } from "@/components/booking/SpacePicker";
import { SlotList } from "@/components/booking/SlotList";
import { BookingSummary } from "@/components/booking/BookingSummary";
import { StepTitle } from "@/components/booking/StepTitle";
import { useAvailability } from "@/hooks/useAvailability";
import { prettyDate } from "@/lib/studio";
import { api, apiError } from "@/lib/api";

const CALENDAR_CLASSES = { day_disabled: "text-zinc-700 opacity-60 line-through" };

export default function Book() {
  const navigate = useNavigate();
  const { date, setDate, iso, avail, reload, disabledDays } = useAvailability();
  const [space, setSpace] = useState(null);
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSlot(null);
  }, [iso]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bookings", { date: iso, slot, space, notes });
      toast.success("Session booked!");
      navigate("/my-bookings");
    } catch (e) {
      toast.error(apiError(e));
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-6 py-16 md:px-12" data-testid="book-page">
      <p className="eyebrow rise">Book Riot Factory</p>
      <h1 className="rise d1 mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Lock your session</h1>
      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-5">
          <section className="surface p-6 md:p-8">
            <StepTitle n={1} title="Choose a space" hint="optional" />
            <SpacePicker value={space} onChange={setSpace} />
          </section>
          <section className="surface p-6 md:p-8" data-testid="date-picker">
            <StepTitle n={2} title="Pick a weekday" hint="Mon – Fri" />
            <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} weekStartsOn={1}
              disabled={disabledDays} className="flex justify-center p-0" classNames={CALENDAR_CLASSES} />
          </section>
        </div>
        <div className="space-y-8 lg:col-span-7">
          <section className="surface p-6 md:p-8">
            <StepTitle n={3} title="Select a time" />
            <p className="mb-5 font-tech text-sm text-zinc-400" data-testid="selected-date-label">{prettyDate(iso)}</p>
            <SlotList avail={avail} slot={slot} onSelect={setSlot} />
            <div className="mt-8 space-y-2">
              <label htmlFor="notes" className="font-tech text-xs uppercase tracking-[0.2em] text-zinc-400">Notes (optional)</label>
              <Textarea id="notes" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What are you creating? Anything we should know?" data-testid="booking-notes-input" />
            </div>
          </section>
          <BookingSummary iso={iso} slot={slot} space={space} submitting={submitting} onSubmit={submit} />
        </div>
      </div>
    </main>
  );
}
